import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// DS
import {
  Image,
  VertButton,
  VertCarousel,
  VertImage,
  VertTypography,
} from '@vertem-core/vertice-ds';

// TYPES
import {
  OrderPropTypes,
  ParentOrderPropTypes,
} from './propTypes';

// STYLES
import 'Assets/styles/default/components/Orders/OrdersList/OrderCardInfosStyles.less';

// UTILS
import { currencyMask } from 'Utils/commons/mask';
import { pointsMask } from 'Utils/commons/points';
import { formatToBrazilianExtended } from 'Utils/commons/date';
import { getOrderStatus, reduceOrderItems } from 'Utils/orders';
import { limitString } from 'Utils/commons/string';
import generateUUID from 'Utils/generateUUID';

// ORDER CARD INFO
const OrderCardInfo = ({
  orderDate,
  orderId,
  status,
  orderItems,
  isPointAndCash,
  totalPointsValue,
  totalCashValue,
}) => {
  /* Hooks */
  const navigate = useNavigate();
  const Catalog = useSelector(({ Catalog: data }) => data);
  /* Vars */
  const formattedDate = formatToBrazilianExtended(orderDate);
  const { label: statusLabel, color: statusColor } = getOrderStatus(status);
  const normalizedItems = reduceOrderItems(orderItems);
  const shouldRenderArrows = normalizedItems.length > 1;
  const {
    currencyConfiguration: { decimalPlaces, pointsName },
  } = Catalog;
  const formattedPointsValue = pointsMask(
    totalPointsValue,
    decimalPlaces,
    pointsName
  );

  const hasFreeItem = formattedPointsValue.value == 0;

  /* Handlers */
  const goToDetailsHandler = (orderId) => {
    if (orderId) {
      navigate(`/order-details?orderId=${orderId}&fromCheckout=${false}`);
    }
  };

  /* Utils */
  const renderOrderItems = (
    <VertCarousel arrows={shouldRenderArrows} dots={false} infinite>
      {normalizedItems.map((item) => (
        <div className="order-items" key={generateUUID()}>
          <div className="item-image">
            <VertImage
              src={item.productImage}
              alt={item.productName}
              preview={false}
              placeholder="https://vertice-ds.s3.amazonaws.com/Assets/Images/fallbacks/fallback-img-store.png"
              fallback="https://vertice-ds.s3.amazonaws.com/Assets/Images/fallbacks/fallback-img-store.png"
            />
          </div>
          <div className="item-infos">
            <span className="item-name">
              {limitString(item.productName, 30)}
            </span>
            <span className="item-vendor">
              {limitString(
                `Fornecido e entregue por ${item.vendorName}`,
                42
              )}
            </span>
              <span className="item-quantity">Itens: ({item.quantity})</span>
            </div>
          </div>
      ))}
    </VertCarousel>
  );

  /* Renders */
  return (<>
    <div className="card-wrapper order-list-like">
      <div className="card-header">
        <span className="date-order">{formattedDate}</span>
      </div>
      <div className="card-content">
        <div className="product-info">
          <div className="product-info-header">
            <span className="identifier-product">#Pedido {orderId}</span>
            <div className="verticalLine"/>
            <div className={`status-order-container ${statusColor} `}>
              <div className="dot-status"/>
              <span className="description-status">{statusLabel}</span>
            </div>
          </div>
          <div
            style={{
              maxWidth: '450px',
            }}
          >
            {renderOrderItems}
          </div>
        </div>

        <div className="order-value-wrapper">
          {isPointAndCash && (
            <VertTypography.Paragraph className="total-order-value">
              <span>{formattedPointsValue.value}</span>{' '}
              {formattedPointsValue.label} +{' '}
              <span>{currencyMask(totalCashValue)}</span>
            </VertTypography.Paragraph>
          )}

          {!isPointAndCash && (
            <VertTypography.Paragraph className="total-order-value">
              <span className={`${hasFreeItem ? "color-highlight" : ""}`}>{hasFreeItem ? "Grátis" : `${formattedPointsValue.value} ${formattedPointsValue.label}`}</span>
            </VertTypography.Paragraph>
          )}
        </div>

        <div className="order-details">
          <VertButton
            className="order-button-details"
            type="highlight"
            onClick={() => goToDetailsHandler(orderId)}
          >
            Exibir detalhes
          </VertButton>
        </div>
      </div>
    </div>
  </>);
};

OrderCardInfo.propTypes = { ...OrderPropTypes, ...ParentOrderPropTypes };

export default OrderCardInfo;
